import { ProcessingOverlay } from '@components'

import { BorderRadius, Color, FormItemsGroup, Link, Modal, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { getEInvoicingInfoLink } from '@features/invoices/utils/getEInvoicingInfoLink'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { useEInvoicingAddressForm } from '../../../../contexts/eInvoicingAddressFormContext'
import { AddressingLineLookupStatus } from '../../../../enums/addressingLineLookupStatus'
import { Attachments } from './elements/Attachments'
import { CustomerCompanyName } from './elements/CustomerCompanyName'
import { CustomerEInvoicingAddress } from './elements/CustomerEInvoicingAddress'
import { EInvoicingAddressingLineNotification } from './elements/EInvoicingAddressingLineNotification'

interface SendAsEInvoiceModalBodyProps {
  attachmentsEnabled: boolean
  isMissingCustomerEInvoicingAddress: boolean
}

export const SendAsEInvoiceModalBody = ({
  attachmentsEnabled,
  isMissingCustomerEInvoicingAddress,
}: SendAsEInvoiceModalBodyProps): ReactElement | null => {
  const { t } = useTranslation()
  const { user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const {
    eInvoicingAddressForm: { Form },
    isLoading: isEInvoicingAddressFormLoading,
    isSubmitting,
    lookupStatus,
  } = useEInvoicingAddressForm()

  const isAddressingLineNotificationVisible =
    isFrenchComplianceMode && lookupStatus === AddressingLineLookupStatus.Unregistered

  const missingEInvoicingDescription = isMissingCustomerEInvoicingAddress
    ? t('external_invoices.send_as_e_invoice.modal.subtitle.provide_identifier')
    : t('external_invoices.send_as_e_invoice.modal.subtitle.review_identifier')

  const shouldShowProcessingOverlay = isEInvoicingAddressFormLoading || isSubmitting

  if (isCurrentUserLoading || !user) {
    return null
  }

  return (
    <Modal.Body>
      {shouldShowProcessingOverlay && (
        <ProcessingOverlay borderRadius={BorderRadius.R12} color={Color.White} withAnimation />
      )}
      <Form>
        <FormItemsGroup rowSpacing={Spacing.S12}>
          <Text>{missingEInvoicingDescription}</Text>
          <CustomerCompanyName />
          <CustomerEInvoicingAddress />

          <Text colorVariant="secondary">
            <Trans
              components={{
                a: (
                  <Link
                    href={getEInvoicingInfoLink(user.locale)}
                    rel="noopener noreferrer"
                    target="_blank"
                    withAnimation
                  ></Link>
                ),
              }}
              i18nKey="external_invoices.send_as_e_invoice.modal.e_invoicing_address.learn_more_link"
            />
          </Text>
          {isAddressingLineNotificationVisible && <EInvoicingAddressingLineNotification />}
        </FormItemsGroup>
        {attachmentsEnabled && <Attachments />}
      </Form>
    </Modal.Body>
  )
}
